import { Grid2 as Grid } from '@mui/material'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import ImageList from '@mui/material/ImageList'
import ImageListItem from '@mui/material/ImageListItem'
import Link from '@mui/material/Link'
import Step from '@mui/material/Step'
import StepContent from '@mui/material/StepContent'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Typography from '@mui/material/Typography'
import moment from 'moment-timezone'
import { useContext } from 'react'
import ICalendarLink from 'react-icalendar-link'

import {
  GENERAL_REMAINDER_MICRONEEDLING,
  GENERAL_REMAINDER_OTHERS,
} from 'utils/constants/Helpers'
import { getFacialType } from 'utils/helper-functions'
import { AppContext } from '../../../providers/context/App'
import {
  FIRST_TIMER_FORM,
  GENERAL_REMINDER,
} from '../../../utils/constants/StaticLinks'
import { getLocationBanner } from '../../../utils/helper-functions/locationBanner'
import { ShareAppointmentCard } from '../shareAppointmentCard'
import { ManageAppointmentButton } from './manage-appointment-button'

import { triggerConfirmationPageEvent } from 'services/Analytics'
import { CONFIRMATION_GA4_EVENTS } from 'utils/constants/Enums'
import { ReactComponent as CallIcon } from '../../../assets/icons/call-new.svg'
import { ReactComponent as MailIcon } from '../../../assets/icons/mail-new.svg'
import { ReactComponent as RoundCheckMark } from '../../../assets/icons/roundcheckmark-new.svg'

import './confirmedAppointment.scss'

const facialSteps = [
  {
    label: '2 Weeks Before',
    description: `Refrain from BOTOX, fillers, chemical peels, or any skin procedures like microneedling, microblading, dermaplaning, or microdermabrasion.`,
  },
  {
    label: '2 Days Before',
    description: `Don't exfoliate, wax, or apply RX-strength topicals.`,
  },
  {
    label: `1  Day Before`,
    description: `Don't clean shave. Come in rocking that 5 o'clock shadow.`,
  },
]

const laserFacialSteps = [
  {
    label: '2 Weeks Before',
    description:
      'Refrain from BOTOX, fillers, or any advanced exfoliation like chemical peels, microdermabrasion, microneedling or microblading.',
  },
  {
    label: '1 Week Before',
    description: 'Discontinue use of any exfoliants, waxing or Rx topicals.',
  },
  {
    label: '1-3 Days Before',
    description:
      'Shave (or book a Dermaplane treatment) to remove any facial hair or peach fuzz for optimal results.',
  },
]

const microNeedlingSteps = [
  {
    label: '2 Weeks Before',
    description: `Refrain from Botox, fillers, IPL, Laser, Chemical Peels and Microblading`,
  },
  {
    label: '1 Week Before',
    description: `Discontinue use of any exfoliants, topical antibiotics, retinoids, and Benzoyl Peroxide. Refrain from waxing, depilatories, electrolysis, and avoid unprotected sun exposure`,
  },
  {
    label: `1  Day Before`,
    description: `Avoid alcohol and caffeine. Do not shave the day of your appointment.`,
  },
]

const ConfirmedAppointmentContent = () => {
  const { appMasterData } = useContext(AppContext)

  const handleAddToCalendar = () => {
    triggerConfirmationPageEvent(
      CONFIRMATION_GA4_EVENTS.CLICK_ADD_TO_CALENDAR,
      appMasterData.userInfo.boulevard_id
    )
  }

  const isLaserFacial = appMasterData.isLaserFacial

  let steps
  if (isLaserFacial) {
    steps = laserFacialSteps
  } else if (appMasterData.isMicroNeedling) {
    steps = microNeedlingSteps
  } else {
    steps = facialSteps
  }

  const calendarEvent = {
    title: `${getFacialType(appMasterData)} ${
      appMasterData.selectedEsthetician.isAny
        ? ''
        : `with ${
            appMasterData.selectedEsthetician.staff.firstName
          } ${appMasterData.selectedEsthetician.staff.lastName.charAt(0)}`
    } `,
    description: `${getFacialType(appMasterData)} ${
      appMasterData.selectedEsthetician.isAny
        ? ''
        : `with ${
            appMasterData.selectedEsthetician.staff.firstName
          } ${appMasterData.selectedEsthetician.staff.lastName.charAt(0)}`
    } `,
    startTime: appMasterData.selectedTime.startTime,
    endTime: moment(appMasterData.selectedTime.startTime)
      .add(50, 'minutes')
      .format(),
    location: `${appMasterData.location.address.streetAddress} ${appMasterData.location.address.city}, ${appMasterData.location.address.state} ${appMasterData.location.address.zipCode}`,
  }
  const locationInformation = appMasterData.location.contact

  return (
    <>
      <div className="success-screen-content">
        <div className="success-screen-wrapper">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }} className="success-header-section">
              <RoundCheckMark className="success-screen-header-icon" />
              <div className="success-header-wrapper">
                <Typography component="div" className="success-screen-header">
                  You're All Set!
                </Typography>
                {appMasterData.cart.purchasedMembership && (
                  <Typography component="p" className="first-member-message">
                    Enjoy your first member facial.
                  </Typography>
                )}
              </div>
            </Grid>
            <Grid size={{ xs: 12 }} className="success-header-img">
              <ImageList>
                <ImageListItem cols={12} rows={12}>
                  <img
                    src={getLocationBanner(appMasterData.location.name)}
                    alt="container-img"
                    loading="lazy"
                  />
                </ImageListItem>
              </ImageList>
            </Grid>
            <Grid size={{ xs: 12 }} className="success-header-facial-selected">
              <h3 className="success-screen-name">
                {getFacialType(appMasterData)}{' '}
                {appMasterData.selectedEsthetician.isAny ? (
                  ''
                ) : (
                  <>
                    <br />
                    with {
                      appMasterData.selectedEsthetician.staff.firstName
                    }{' '}
                    {appMasterData.selectedEsthetician.staff.lastName.charAt(0)}
                    .
                  </>
                )}
              </h3>
              <Typography component="div" className="success-screen-date">
                {moment(appMasterData.selectedDate).format('dddd, MMM DD')} @{' '}
                {moment
                  .tz(
                    appMasterData.selectedTime.startTime,
                    appMasterData.location.tz
                  )
                  .format('h:mm A')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }} className="add-to-calender">
              {/* @ts-ignore */}
              <ICalendarLink event={calendarEvent} className="ical-link">
                <Button
                  variant="outlined"
                  className="success-screen-btn"
                  color="primary"
                  onClick={handleAddToCalendar}
                >
                  Add to Calendar
                </Button>
              </ICalendarLink>
              <div className="manage-appointment-link">
                <ManageAppointmentButton />
              </div>
            </Grid>

            {/* not showing share appointment card if user purchased membership along with appointment */}
            {/* it'll show share appointment card if only appointment was purchased */}
            {!appMasterData.isLaserFacial &&
              !appMasterData.cart.purchasedMembership && (
                <Grid size={{ xs: 12 }} className="share-appointment-section">
                  <ShareAppointmentCard />
                </Grid>
              )}

            <Grid size={{ xs: 12 }} className="divider-wrapper">
              <Divider className="divider" />
            </Grid>

            <Grid size={{ xs: 12 }} className="location-address-wrapper">
              <div className="success-screen-address1">
                {appMasterData.location.name}
              </div>
              <div className="success-screen-address2">
                {`${appMasterData.location.address.streetAddress} ${appMasterData.location.address.city}, ${appMasterData.location.address.state} ${appMasterData.location.address.zipCode}`}
              </div>
              {!!locationInformation && (
                <div className="success-screen-icon-box">
                  <CallIcon className="success-screen-icon" />
                  <a
                    href={`tel:${locationInformation?.phone}`}
                    className="success-screen-link-text"
                  >
                    {locationInformation?.phone}
                  </a>
                </div>
              )}
              {!!locationInformation && (
                <div className="success-screen-icon-box">
                  <MailIcon className="success-screen-icon" />
                  <a
                    href={`mailto:${locationInformation?.email}`}
                    className="success-screen-link-text"
                    target={'_blank'}
                    rel="noreferrer"
                  >
                    {locationInformation?.email}
                  </a>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
      <div className="content-body tertiary-background">
        <Grid container spacing={2} className="success-content">
          <Grid size={{ xs: 12 }} className="success-content-wrapper">
            <Typography component="div" className="success-screen-content-head">
              The countdown begins!
              <br />
              Let's get your skin ready
            </Typography>
            <Grid size={{ xs: 12 }}>
              <Stepper orientation="vertical" className="stepper-wrapper">
                {steps.map((step) => (
                  <Step active={true} key={step.label}>
                    <StepLabel className="stepper-label">
                      {step.label}
                    </StepLabel>
                    <StepContent>
                      <div className="step-content">{step.description}</div>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid size={{ xs: 12 }} className="success-reminder">
              <Typography className="success-reminder-head">
                General Reminders
              </Typography>
              {appMasterData.isMicroNeedling && (
                <>
                  <Grid
                    size={{ xs: 12 }}
                    className="success-reminder-container"
                  >
                    <div className="success-reminder-content">
                      {GENERAL_REMAINDER_MICRONEEDLING}
                    </div>
                  </Grid>
                  <Grid
                    size={{ xs: 12 }}
                    className="success-reminder-container"
                  >
                    <div className="success-reminder-content">
                      New clients, please complete our{' '}
                      <Link
                        href={FIRST_TIMER_FORM}
                        target={`_blank`}
                        rel="noreferrer"
                      >
                        first-time facial form
                      </Link>{' '}
                      ahead of time so we can craft a made-for-you facial
                      experience. First-time Microneedling clients must complete
                      the required Microneedling forms, which you'll receive by
                      email prior to arrival.
                    </div>
                  </Grid>
                </>
              )}

              {!appMasterData.isMicroNeedling && (
                <>
                  <Grid
                    size={{ xs: 12 }}
                    className="success-reminder-container"
                  >
                    <div className="success-reminder-content">
                      {GENERAL_REMAINDER_OTHERS}
                    </div>
                  </Grid>
                  <Grid
                    size={{ xs: 12 }}
                    className="success-reminder-container"
                  >
                    <div className="success-reminder-content">
                      New clients, please complete our{' '}
                      <Link
                        href={FIRST_TIMER_FORM}
                        target={`_blank`}
                        rel="noreferrer"
                      >
                        {' '}
                        {isLaserFacial
                          ? 'first-time facial'
                          : 'first-timer'}{' '}
                        form
                      </Link>{' '}
                      {isLaserFacial
                        ? "ahead of time so we can learn about your skin. First-time Laser clients must complete the Laser Waiver you'll receive by email, prior to arrival."
                        : 'ahead of time for a speedy check-in process.'}
                    </div>
                  </Grid>
                </>
              )}

              {!isLaserFacial && !appMasterData.isMicroNeedling && (
                <>
                  <Grid
                    size={{ xs: 12 }}
                    className="success-reminder-container"
                  >
                    <div className="success-reminder-content">
                      Keep your neck clear of high collars and jewelry, because
                      necks need love too.
                    </div>
                  </Grid>
                  <Grid
                    size={{ xs: 12 }}
                    className="success-reminder-container"
                  >
                    <div className="success-reminder-content">
                      Know your home care routine and any medications you're
                      taking. Your Esthetician will want to know as much as
                      possible.
                    </div>
                  </Grid>
                  <Grid
                    size={{ xs: 12 }}
                    className="success-reminder-container"
                  >
                    <div className="success-reminder-content">
                      Age Minimum: We don't perform facials on anyone 14 years
                      old or under, and recommend seeing a dermatologist for any
                      serious skin issues. Please read our full age policy{' '}
                      <Link
                        href={GENERAL_REMINDER}
                        target="_blank"
                        rel="noreferrer"
                      >
                        here.
                      </Link>
                    </div>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </div>

      <Grid container spacing={2} className="footer-wrapper">
        <Grid size={{ xs: 12 }} className="footer-content-wrapper">
          <Grid size={{ xs: 12 }}>
            <div className="footer-head">Cancellation Policy</div>
            <div className="footer-content-container">
              <div className="footer-content">
                Appointments may be cancelled at no charge up to 24 hrs before
                the start time of the reservation. Otherwise a $25 fee will be
                assessed.
              </div>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default ConfirmedAppointmentContent
